import { useEffect, useRef } from "react";
import type {
  Job,
  RoleLevelPreference,
  TimeRange,
  VisitFilter,
} from "../types/job";
import { FilterMenu } from "./FilterMenu";
import { JobCard } from "./JobCard";

function visitFilterEmptyTitle(filter: VisitFilter): string {
  if (filter === "visited") return "No applied jobs in this view";
  return "Every job here is already applied";
}

function postedWithinPhrase(timeRange: TimeRange): string {
  return timeRange === "24h" ? "24 hours" : "7 days";
}

type JobListProps = {
  jobs: Job[];
  /** Count after search / date / role filters (before visit filter). */
  baseMatchCount: number;
  /** Count after search / date only (ignores role filter). */
  matchesBeforeRoleFilter: number;
  roleLevel: RoleLevelPreference;
  onRoleLevelChange: (v: RoleLevelPreference) => void;
  timeRange: TimeRange;
  onTimeRangeChange: (v: TimeRange) => void;
  visitFilter: VisitFilter;
  onVisitFilterChange: (v: VisitFilter) => void;
  selectedJobId: string | null;
  hoveredJobId: string | null;
  isJobVisited: (jobId: string) => boolean;
  onJobListingOpened: (jobId: string) => void;
  onHoverJob: (id: string) => void;
  onLeaveJob: () => void;
  onSelectJobOnMap: (id: string) => void;
};

export function JobList({
  jobs,
  baseMatchCount,
  matchesBeforeRoleFilter,
  roleLevel,
  onRoleLevelChange,
  timeRange,
  onTimeRangeChange,
  visitFilter,
  onVisitFilterChange,
  selectedJobId,
  hoveredJobId,
  isJobVisited,
  onJobListingOpened,
  onHoverJob,
  onLeaveJob,
  onSelectJobOnMap,
}: JobListProps) {
  const refs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!selectedJobId) return;
    refs.current[selectedJobId]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [selectedJobId]);

  const emptyFromVisitFilter =
    jobs.length === 0 && baseMatchCount > 0 && visitFilter !== "all";

  const emptyFromRoleFilter =
    jobs.length === 0 &&
    baseMatchCount === 0 &&
    matchesBeforeRoleFilter > 0 &&
    visitFilter === "all" &&
    roleLevel !== "all";

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <div className="sticky top-0 z-10 border-b border-slate-100 bg-white/95 px-4 py-3 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 text-sm text-slate-600">
            <span className="font-semibold tabular-nums text-slate-900">
              {jobs.length}
            </span>{" "}
            {jobs.length === 1 ? "job" : "jobs"} posted within{" "}
            {postedWithinPhrase(timeRange)}
          </p>
          <FilterMenu
            timeRange={timeRange}
            onTimeRangeChange={onTimeRangeChange}
            visitFilter={visitFilter}
            onVisitFilterChange={onVisitFilterChange}
            roleLevel={roleLevel}
            onRoleLevelChange={onRoleLevelChange}
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-4">
        {jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/80 py-16 text-center">
            {emptyFromVisitFilter ? (
              <>
                <p className="text-sm font-medium text-slate-700">
                  {visitFilterEmptyTitle(visitFilter)}
                </p>
                <p className="mt-2 max-w-[260px] text-xs leading-relaxed text-slate-500">
                  Try <span className="font-medium text-slate-600">All</span>{" "}
                  under{" "}
                  <span className="font-medium text-slate-600">
                    Job listings
                  </span>{" "}
                  in the filter menu, or open postings with{" "}
                  <span className="font-medium text-slate-600">Apply</span> —
                  applied jobs are remembered for seven days.
                </p>
              </>
            ) : emptyFromRoleFilter ? (
              <>
                <p className="text-sm font-medium text-slate-700">
                  No jobs match your role focus here
                </p>
                <p className="mt-2 max-w-[260px] text-xs leading-relaxed text-slate-500">
                  Try{" "}
                  <span className="font-medium text-slate-600">All levels</span>{" "}
                  or another role in the filter menu, or pan the map — titles
                  are matched with simple keywords.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-slate-700">
                  No jobs found in this area
                </p>
                <p className="mt-2 max-w-[240px] text-xs leading-relaxed text-slate-500">
                  Pan or zoom the map, adjust filters next to the job count, or
                  clear your search.
                </p>
              </>
            )}
          </div>
        ) : (
          jobs.map((job) => (
            <JobCard
              key={job.id}
              ref={(el) => {
                refs.current[job.id] = el;
              }}
              job={job}
              isHighlighted={
                selectedJobId === job.id || hoveredJobId === job.id
              }
              isVisited={isJobVisited(job.id)}
              onHover={() => onHoverJob(job.id)}
              onLeave={onLeaveJob}
              onSelectOnMap={() => onSelectJobOnMap(job.id)}
              onJobListingOpened={() => onJobListingOpened(job.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}
